import { getFeatureCatalogLeafMetadata } from '@/utility/featureCatalogLeafMetadata'
import { getInsightsProductReleasePath } from '@/utility/productReleasePaths'

export const LANE_PERFORMANCE_INSIGHTS_TYPE = 'lane-performance'
export const LANE_PERFORMANCE_WAYPOINT_RPC_INSIGHTS_TYPE = 'lane-performance-waypoint'

export const LANE_PERFORMANCE_INSIGHTS_GATE = 'fe-571-enable-lane-performance-insights'
export const LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY = 'insights_laneperformance'
export const INSIGHTS_COMMAND_CENTER_USER_GROUP_FEATURE_KEY = 'insights_commandcenter'

export function shouldShowInsightsCommandCenterUserGroupFeature(featureKey) {
  return featureKey !== INSIGHTS_COMMAND_CENTER_USER_GROUP_FEATURE_KEY
}

function readInsightsFeaturePermissions() {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(localStorage.featurePermissions || '{}')
  } catch {
    return {}
  }
}

export function getStoredInsightsFeaturePermissions() {
  return readInsightsFeaturePermissions()
}

/** Fallback route when Insights Command Center is unavailable. */
export function getInsightsCommandCenterFallbackRoute(statsigClient) {
  const insightsPath = getInsightsProductReleasePath(statsigClient)
  const permissions = readInsightsFeaturePermissions()
  if (permissions?.insights_temprelease) return insightsPath
  if (isLanePerformanceMenuVisible(permissions)) {
    return getLanePerformanceEntryRoute(permissions, statsigClient)
  }
  return insightsPath
}

/**
 * Lane Performance visible in Insights menu and routable when the user-group
 * permission `insights_laneperformance` is present.
 */
export function isLanePerformanceMenuVisible(featurePermissions) {
  return isLanePerformanceInsightsAccessEnabled(featurePermissions)
}

/** Default landing route when navigating to /insights/lane-performance */
export function getLanePerformanceEntryRoute(featurePermissions, statsigClient) {
  if (isLanePerformanceInsightsAccessEnabled(featurePermissions)) {
    return '/insights/lane-performance/executiveSummary'
  }

  return getInsightsProductReleasePath(statsigClient)
}

export function isLanePerformanceLightdashDashboardEnabled(checkGate) {
  if (typeof checkGate !== 'function') return false
  return checkGate(LANE_PERFORMANCE_INSIGHTS_GATE)
}

export function isLanePerformanceOrgFeatureEnabled(orgFeatures) {
  if (!orgFeatures || typeof orgFeatures !== 'object') return false
  return Boolean(orgFeatures[LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY])
}

/** UAC catalog row: org has `insights_laneperformance` in default_organization_features. */
export function isLanePerformanceUserGroupCatalogVisible(orgFeatures) {
  return isLanePerformanceOrgFeatureEnabled(orgFeatures)
}

export function shouldShowLanePerformanceUserGroupFeature(featureKey, orgFeatures) {
  if (featureKey !== LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY) return true
  return isLanePerformanceUserGroupCatalogVisible(orgFeatures)
}

function shouldShowFeatureInUserGroupCatalog(featureKey, orgFeatures) {
  return (
    shouldShowInsightsCommandCenterUserGroupFeature(featureKey) &&
    shouldShowLanePerformanceUserGroupFeature(featureKey, orgFeatures)
  )
}

export function applyFeatureCatalogVisibilityRules(catalog, orgFeatures) {
  if (!Array.isArray(catalog)) return []

  return catalog
    .map((group) => ({
      ...group,
      features: (group.features || []).filter((field) =>
        shouldShowFeatureInUserGroupCatalog(field?.key, orgFeatures),
      ),
    }))
    .filter((group) => (group.features || []).length > 0)
}

function buildLanePerformanceCatalogLeaf() {
  const meta = getFeatureCatalogLeafMetadata(LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY)
  if (!meta) return null

  return {
    key: LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY,
    title: meta.title,
    description: meta.description,
    required: meta.required ?? false,
    icon: meta.icon,
  }
}

function enrichLanePerformanceCatalogLeaf(field, leaf) {
  if (!field || field.key !== LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY || !leaf) return field

  return {
    ...field,
    title: field.title || leaf.title,
    description: field.description || leaf.description,
    icon: field.icon || leaf.icon,
    required: field.required ?? leaf.required ?? false,
  }
}

function sortInsightsFeaturesWithLanePerformanceFirst(features) {
  if (!Array.isArray(features)) {
    return features
  }

  const lanePerformance = features.find(
    (field) => field?.key === LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY,
  )
  if (!lanePerformance) return features

  const otherFeatures = features.filter(
    (field) => field?.key !== LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY,
  )
  return [lanePerformance, ...otherFeatures]
}

export function injectLanePerformanceIntoFeatureCatalog(catalog, orgFeatures) {
  if (!isLanePerformanceUserGroupCatalogVisible(orgFeatures) || !Array.isArray(catalog)) {
    return catalog
  }

  const leaf = buildLanePerformanceCatalogLeaf()
  if (!leaf) return catalog

  const meta = getFeatureCatalogLeafMetadata(LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY)
  let hasInsightsGroup = false

  const nextCatalog = catalog.map((group) => {
    if (group?.name !== meta.groupName) return group

    hasInsightsGroup = true
    const existingKeys = new Set((group.features || []).map((field) => field?.key))
    if (existingKeys.has(LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY)) {
      return {
        ...group,
        features: sortInsightsFeaturesWithLanePerformanceFirst(
          (group.features || []).map((field) => enrichLanePerformanceCatalogLeaf(field, leaf)),
        ),
      }
    }

    const nextFeatures = [...(group.features || []), leaf]
    return {
      ...group,
      features: sortInsightsFeaturesWithLanePerformanceFirst(nextFeatures),
    }
  })

  if (hasInsightsGroup) return nextCatalog

  return [
    ...nextCatalog,
    {
      name: meta.groupName,
      icon: meta.icon,
      features: [leaf],
    },
  ]
}

/** Returns org features unchanged — Lane Performance UAC visibility requires an explicit org flag. */
export function resolveLanePerformanceOrgFeatureAvailability(orgFeatures) {
  return orgFeatures
}

export function hasLanePerformanceUserGroupAccess() {
  if (typeof window === 'undefined') return false

  try {
    const permissions = JSON.parse(localStorage.featurePermissions || '{}')
    return Boolean(permissions?.[LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY])
  } catch {
    return false
  }
}

export function hasLanePerformanceUserGroupPermission(featurePermissions) {
  if (featurePermissions && typeof featurePermissions === 'object') {
    return Boolean(featurePermissions[LANE_PERFORMANCE_USER_GROUP_FEATURE_KEY])
  }
  return hasLanePerformanceUserGroupAccess()
}

/**
 * Lane Performance menu/route access: user-group `insights_laneperformance`.
 */
export function isLanePerformanceInsightsAccessEnabled(featurePermissions) {
  return hasLanePerformanceUserGroupPermission(featurePermissions)
}

export function isLanePerformanceAccessEnabled() {
  return isLanePerformanceInsightsAccessEnabled()
}

/**
 * When the Lightdash lane-performance route is not accessible, return redirect target.
 * Returns null when Lightdash access is allowed.
 */
export function getLanePerformanceAccessDeniedRoute(featurePermissions, statsigClient) {
  if (isLanePerformanceInsightsAccessEnabled(featurePermissions)) {
    return null
  }

  return getInsightsCommandCenterFallbackRoute(statsigClient)
}

export const DASHBOARD_KEYS = {
  EXECUTIVE_SUMMARY: 'executiveSummary',
  CARRIERS: 'carriers',
  WAYPOINT_HOTSPOTS: 'waypointHotspots',
  COMPLIANCE: 'compliance',
  IOT_DEVICE_PERFORMANCE: 'iotDevicePerformance',
  CO2: 'co2',
}

export const DEFAULT_DASHBOARD_KEY = DASHBOARD_KEYS.EXECUTIVE_SUMMARY

export function getLanePerformanceRpcInsightsType(dashboardKey) {
  return dashboardKey === DASHBOARD_KEYS.WAYPOINT_HOTSPOTS
    ? LANE_PERFORMANCE_WAYPOINT_RPC_INSIGHTS_TYPE
    : LANE_PERFORMANCE_INSIGHTS_TYPE
}

/** Lane Performance Lightdash project — configured via VITE_LIGHTDASH_PROJECT_LANE_PERFORMANCE_UUID */
export const LANE_PERFORMANCE_PROJECT_UUID = import.meta.env
  .VITE_LIGHTDASH_PROJECT_LANE_PERFORMANCE_UUID

export const DASHBOARD_UUIDS = {
  [DASHBOARD_KEYS.EXECUTIVE_SUMMARY]: import.meta.env
    .VITE_LIGHTDASH_DASHBOARD_LANE_PERFORMANCE_EXECUTIVE_SUMMARY_UUID,
  [DASHBOARD_KEYS.CARRIERS]: import.meta.env
    .VITE_LIGHTDASH_DASHBOARD_LANE_PERFORMANCE_CARRIERS_UUID,
  [DASHBOARD_KEYS.WAYPOINT_HOTSPOTS]: import.meta.env
    .VITE_LIGHTDASH_DASHBOARD_LANE_PERFORMANCE_WAYPOINT_HOTSPOTS_UUID,
  [DASHBOARD_KEYS.COMPLIANCE]: import.meta.env
    .VITE_LIGHTDASH_DASHBOARD_LANE_PERFORMANCE_COMPLIANCE_UUID,
  [DASHBOARD_KEYS.IOT_DEVICE_PERFORMANCE]: import.meta.env
    .VITE_LIGHTDASH_DASHBOARD_LANE_PERFORMANCE_IOT_DEVICE_PERFORMANCE_UUID,
  [DASHBOARD_KEYS.CO2]: import.meta.env.VITE_LIGHTDASH_DASHBOARD_LANE_PERFORMANCE_CO2_UUID,
}

export const DASHBOARD_OPTIONS = [
  { value: DASHBOARD_KEYS.EXECUTIVE_SUMMARY, label: 'Global Executive' },
  { value: DASHBOARD_KEYS.CARRIERS, label: 'Carrier Benchmarking' },
  { value: DASHBOARD_KEYS.WAYPOINT_HOTSPOTS, label: 'Waypoint Hotspots' },
  { value: DASHBOARD_KEYS.COMPLIANCE, label: 'Deviation & Compliance' },
  { value: DASHBOARD_KEYS.IOT_DEVICE_PERFORMANCE, label: 'IoT Performance' },
  { value: DASHBOARD_KEYS.CO2, label: 'CO2 Emission' },
]

const DASHBOARD_LABEL_BY_KEY = Object.fromEntries(
  DASHBOARD_OPTIONS.map((option) => [option.value, option.label]),
)

export function getDashboardLabel(dashboardKey) {
  return DASHBOARD_LABEL_BY_KEY[dashboardKey] || DASHBOARD_LABEL_BY_KEY[DEFAULT_DASHBOARD_KEY]
}

export function getDashboardUuid(dashboardKey) {
  return DASHBOARD_UUIDS[dashboardKey] || DASHBOARD_UUIDS[DEFAULT_DASHBOARD_KEY]
}

/** Lightdash project UUID for Lane Performance (VITE_LIGHTDASH_PROJECT_LANE_PERFORMANCE_UUID). */
export function getLanePerformanceProjectUuid() {
  return import.meta.env.VITE_LIGHTDASH_PROJECT_LANE_PERFORMANCE_UUID
}

export function buildLanePerformanceDashboardUuids() {
  const projectUuid = getLanePerformanceProjectUuid()
  if (!projectUuid) return []

  return Object.values(DASHBOARD_UUIDS)
    .filter(Boolean)
    .map((dashboard_uuid) => ({
      project_uuid: projectUuid,
      dashboard_uuid,
    }))
}

export function isValidDashboardKey(dashboardKey) {
  return Object.prototype.hasOwnProperty.call(DASHBOARD_UUIDS, dashboardKey)
}

export function resolveDashboardKeyFromParam(dashboardKey) {
  if (dashboardKey && isValidDashboardKey(dashboardKey)) return dashboardKey
  return DEFAULT_DASHBOARD_KEY
}

export function getLanePerformanceDashboardIndex(dashboardKey) {
  return DASHBOARD_OPTIONS.findIndex((option) => option.value === dashboardKey)
}

export function getAdjacentLanePerformanceDashboardKey(dashboardKey, direction) {
  const index = getLanePerformanceDashboardIndex(dashboardKey)
  if (index < 0) return null

  const nextIndex = direction === 'next' ? index + 1 : index - 1
  if (nextIndex < 0 || nextIndex >= DASHBOARD_OPTIONS.length) return null
  return DASHBOARD_OPTIONS[nextIndex].value
}
